/**
 * v2.0.1 StatusNotification wire mapping.
 *
 * v1.6 carries nine connector statuses; v2.0.1 collapses them to five
 * (`Available, Occupied, Reserved, Unavailable, Faulted`) per OCPP 2.0.1
 * Part 1 §3.8.2. The session-active statuses (Preparing, Charging,
 * SuspendedEV, SuspendedEVSE, Finishing) all map to `Occupied`.
 *
 * The wire also gains an `evseId` field — the domain
 * `StatusNotificationDomain` carries only `connectorId`, so this layer
 * defaults `evseId = 1` (single-EVSE topology, which is the only one
 * Session 10a exercises). Multi-EVSE topology mapping is Session 10b.
 */
import { ConnectorStatus } from '@/domain/types';
import type { StatusNotificationDomain } from '@/protocol/OcppAdapter';

export type ConnectorStatusV201 =
  | 'Available'
  | 'Occupied'
  | 'Reserved'
  | 'Unavailable'
  | 'Faulted';

export function toV201ConnectorStatus(status: ConnectorStatus): ConnectorStatusV201 {
  switch (status) {
    case ConnectorStatus.Available:
      return 'Available';
    case ConnectorStatus.Reserved:
      return 'Reserved';
    case ConnectorStatus.Unavailable:
      return 'Unavailable';
    case ConnectorStatus.Faulted:
      return 'Faulted';
    case ConnectorStatus.Preparing:
    case ConnectorStatus.Charging:
    case ConnectorStatus.SuspendedEV:
    case ConnectorStatus.SuspendedEVSE:
    case ConnectorStatus.Finishing:
      return 'Occupied';
  }
}

const DEFAULT_EVSE_ID = 1;

export function buildStatusNotificationRequest(
  req: StatusNotificationDomain,
): Record<string, unknown> {
  return {
    timestamp: req.timestamp ?? new Date().toISOString(),
    connectorStatus: toV201ConnectorStatus(req.status),
    evseId: DEFAULT_EVSE_ID,
    connectorId: req.connectorId,
  };
}
